import React from "react"
import { Section, SingleShorty, TwoColumnRow } from "./Shared"
import DefaultMarkdown from "../DefaultMarkdown"
import { useFormContext } from "react-hook-form"
import { useSelector } from "react-redux"
import { CountryMap } from "content/CountryMap"
import { getIsUsa, getUserInfo } from "reducers/userInfo"
import L10n from "../../../lib/carrot-l10n"
import { Spacer } from "../atoms/Atoms"
import { useToggle } from "utils/Hooks"
import { IbanDetailsModal, AbaRoutingBicOrSwiftDetailsModal, SortCodeDetailsModal } from "./BankDetailModals"
import { MultiPathSVG } from "../SVG"
import { CompleteFormSelect } from "../atoms/forms/FormSelect"
import { CompleteFormInput } from "components/views/atoms/forms/FormInput"
import { StylelessButton } from "components/views/molecules/Molecules"

const AccountTypes = [
  { label: "Checking", id: "Checking" },
  { label: "Savings", id: "Savings" }
]

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const AccountType = () => {
  const { register, errors } = useFormContext()

  return (
    <CompleteFormSelect
      label={l`client.accountSettings.accountTypeLabel`}
      name="accountType"
      options={AccountTypes}
      backgroundColor="bg-white"
      errors={errors}
      ref={register}
    />
  )
}

const RoutingNumber = () => {
  const { routingNumber } = useSelector(getUserInfo)
  const { errors, register } = useFormContext()

  return (
    <CompleteFormInput
      name="routingNumber"
      label={l`client.accountSettings.routingNumberLabel`}
      placeholder={routingNumber}
      errors={errors}
      maxLength={9}
      ref={register({
        pattern: {
          value: /^\d{9}$/i,
          message: l`client.accountSettings.routingNumberInvalid`
        }
      })}
    />
  )
}

const BankName = () => {
  const { register, errors } = useFormContext()

  return (
    <CompleteFormInput name="bankName" label={l`client.accountSettings.bankNameLabel`} errors={errors} ref={register} />
  )
}

const AccountNumber = () => {
  const { register, errors } = useFormContext()
  const { accountNumber } = useSelector(getUserInfo)

  return (
    <CompleteFormInput
      name="accountNumber"
      label={l`client.accountSettings.accountNumberLabel`}
      placeholder={accountNumber}
      errors={errors}
      maxLength={20}
      ref={register({
        pattern: {
          value: /^[0-9]*$/i,
          message: l`client.accountSettings.accountNumberInvalid`
        }
      })}
    />
  )
}

const FullNameOnAccount = () => {
  const { errors, register } = useFormContext()

  return (
    <CompleteFormInput
      name="bankFullNameOnAccount"
      label={l`client.accountSettings.bankBullNameOnAccountLabel`}
      errors={errors}
      ref={register}
    />
  )
}

const Iban = () => {
  const { register, errors } = useFormContext()
  const { bankIban } = useSelector(getUserInfo)

  return (
    <CompleteFormInput
      name="bankIban"
      placeholder={bankIban}
      errors={errors}
      ref={register}
      label={
        <BankDetailsModalLabel
          name="bankIban"
          text={l`client.accountSettings.bankIbanLabel`}
          modalComponent={IbanDetailsModal}
        />
      }
    />
  )
}

const CountryOptions = Object.keys(CountryMap).map((key) => ({
  label: CountryMap[key],
  id: key
}))

const BankCountryCode = () => {
  const { errors, register } = useFormContext()

  return (
    <CompleteFormSelect
      label={l`client.accountSettings.bankCountryCodeLabel`}
      name="bankCountryCode"
      errors={errors}
      ref={register}
      options={CountryOptions}
    />
  )
}

const BankCityAndState = () => {
  const { errors, register } = useFormContext()

  return (
    <CompleteFormInput
      name="bankCityAndState"
      label={l`client.accountSettings.bankCityAndStateLabel`}
      errors={errors}
      ref={register}
    />
  )
}

const AbaRoutingBicOrSwift = () => {
  const { errors, register } = useFormContext()
  const { bankAbaRoutingBicOrSwift } = useSelector(getUserInfo)

  return (
    <CompleteFormInput
      name="bankAbaRoutingBicOrSwift"
      placeholder={bankAbaRoutingBicOrSwift}
      errors={errors}
      ref={register}
      label={
        <BankDetailsModalLabel
          name="bankAbaRoutingBicOrSwift"
          text={l`client.accountSettings.bankAbaRoutingBicOrSwiftLabel`}
          modalComponent={AbaRoutingBicOrSwiftDetailsModal}
        />
      }
    />
  )
}

const SortCode = () => {
  const { errors, register } = useFormContext()

  return (
    <CompleteFormInput
      name="bankSortCode"
      errors={errors}
      ref={register}
      label={
        <BankDetailsModalLabel
          name="bankSortCode"
          text={l`client.accountSettings.bankSortCodeLabel`}
          modalComponent={SortCodeDetailsModal}
        />
      }
    />
  )
}

const BankDetailsModalLabel = ({ text, name, modalComponent }) => {
  const [isOpen, toggleIsOpen] = useToggle(false)

  return (
    <>
      <div className="flex items-center">
        {text}
        <Spacer width={0.5} />
        <StylelessButton onClick={toggleIsOpen} id={`international-bank-details-${name}-help-icon`}>
          <MultiPathSVG onClick={toggleIsOpen} className="dib pointer black-40" iconName="icn-help" />
        </StylelessButton>
      </div>
      {isOpen && React.createElement(modalComponent, { onExit: toggleIsOpen })}
    </>
  )
}

const InternationalBankDetails = () => (
  <Section
    title="Bank details"
    description={<DefaultMarkdown source={l`account.internationalbankdetails.description`} />}
  >
    <TwoColumnRow left={<FullNameOnAccount />} right={<Iban />} />
    <TwoColumnRow left={<BankName />} right={<AccountNumber />} />
    <TwoColumnRow left={<BankCountryCode />} right={<BankCityAndState />} />
    <TwoColumnRow left={<AbaRoutingBicOrSwift />} right={<SortCode />} />
    <SingleShorty>
      <AccountType />
    </SingleShorty>
  </Section>
)

const UsaBankDetails = () => (
  <Section title="Bank details" description={<DefaultMarkdown source={l`account.bankdetails.description`} />}>
    <TwoColumnRow left={<BankName />} right={<AccountType />} />
    <TwoColumnRow left={<AccountNumber />} right={<RoutingNumber />} />
  </Section>
)

export const BankDetails = () => {
  const isUsa = useSelector(getIsUsa)

  return <div id="bankdetails">{isUsa ? <UsaBankDetails /> : <InternationalBankDetails />}</div>
}
